/**
 * @file usePendingMark.ts
 * @description Отметка строки сборки, которая ждёт решения кладовщика
 *   (phase19.22, W17-9c, W14-9): «партия другая — заменить?» и «отсканируйте
 *   ячейку» (`requireBinScanOnPick`).
 *
 * Зачем отдельный хук: сценарий «замена партии с причиной → сервер требует
 * скан ячейки → скан `LOC:` → отметка» проходит через три запроса, и отметка
 * не должна потерять ни одного параметра по дороге. Поэтому отложенная
 * отметка хранит ВСЁ, с чем её отправляли (`confirmLotMismatch`, `reason`), а
 * повтор разбирает ответ тем же `execute`, что и обычный скан: если и повтор
 * ответит «партия другая», откроется тот же диалог замены.
 *
 * Жизнь отложенной отметки: снимается новым сканом товара, ручной отметкой
 * другой строки, сменой задания (уход с экрана — вместе с хуком). Последний
 * отсканированный адрес уходит в каждую отметку как `confirmedBinCode`.
 */

import { useEffect, useState } from "react";

import { ApiError } from "../../../lib/api-client";
import {
  pickingApi,
  type PickingScanLotRef,
  type PickingScanOptions,
  type PickingTaskDetailDto,
} from "../../../lib/api-picking";

/** Параметры отметки, которые обязаны пережить повтор. */
export type PendingScanOptions = Pick<PickingScanOptions, "confirmLotMismatch" | "reason">;

/** Отметка, ждущая скана ячейки (`bin_scan_required`). */
export type PendingMark =
  | { type: "scan"; code: string; options: PendingScanOptions }
  | { type: "pick"; itemId: string; qty: number };

/** «Партия другая» — ждём решения кладовщика (W17-9c). */
export interface PickingLotMismatch {
  code: string;
  itemId: string;
  expected: PickingScanLotRef;
  scanned: PickingScanLotRef;
}

/** Адрес из `bin_scan_required` (`details.binCode`); не та ошибка — `null`. */
export function binRequiredCode(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.code !== "bin_scan_required") return null;
  const details = err.details as { binCode?: unknown } | undefined;
  return typeof details?.binCode === "string" ? details.binCode : "";
}

interface Handlers {
  /** Строка отмечена: новое задание и id строки (подсветка). */
  onMarked: (task: PickingTaskDetailDto, itemId: string) => void;
  /** Задание пришло без отметки (ждём замены партии). */
  onTask: (task: PickingTaskDetailDto) => void;
  /** Отсканирована ячейка, отметок не ждущая, — показать адрес. */
  onBin: (bin: { code: string; label: string | null }) => void;
}

export function usePendingMark(taskId: string | undefined, handlers: Handlers) {
  const [confirmedBin, setConfirmedBin] = useState<string | null>(null);
  const [pending, setPending] = useState<{ binCode: string; mark: PendingMark } | null>(null);
  const [lotMismatch, setLotMismatch] = useState<PickingLotMismatch | null>(null);

  // Другое задание — чужие отметки и адрес не переносим.
  useEffect(() => {
    setConfirmedBin(null);
    setPending(null);
    setLotMismatch(null);
  }, [taskId]);

  /** Отметка строки с подтверждённым адресом; ответ разбирает `execute`. */
  async function executePick(itemId: string, qty: number, binCode: string | null) {
    if (!taskId) return;
    const task = await pickingApi.pickItem(taskId, itemId, {
      qtyPicked: qty,
      ...(binCode ? { confirmedBinCode: binCode } : {}),
    });
    handlers.onMarked(task, itemId);
  }

  /**
   * Один скан: запрос и разбор ответа. Ячейка — запомнить адрес и повторить
   * отметку, ждавшую именно её; строка без отметки — диалог замены партии.
   * Ошибки не ловит — их показывает вызывающий.
   */
  async function execute(code: string, options: PickingScanOptions = {}): Promise<void> {
    if (!taskId) return;
    const binCode = options.confirmedBinCode ?? confirmedBin ?? undefined;
    const result = await pickingApi.scan(taskId, code, { ...options, confirmedBinCode: binCode });
    if (result.kind === "bin") {
      setConfirmedBin(result.bin.code);
      const waiting = pending && pending.binCode.toUpperCase() === result.bin.code.toUpperCase() ? pending.mark : null;
      if (!waiting) {
        handlers.onBin(result.bin);
        return;
      }
      setPending(null);
      if (waiting.type === "scan") await execute(waiting.code, { ...waiting.options, confirmedBinCode: result.bin.code });
      else await executePick(waiting.itemId, waiting.qty, result.bin.code);
      return;
    }
    if (result.marked === false && result.warning?.code === "lot_mismatch") {
      handlers.onTask(result.task);
      setLotMismatch({ code, itemId: result.itemId, expected: result.warning.expected, scanned: result.warning.scanned });
      return;
    }
    handlers.onMarked(result.task, result.itemId);
  }

  /** Сервер потребовал скан ячейки — запомнить отметку целиком. */
  function deferIfBinRequired(err: unknown, mark: PendingMark): boolean {
    const binCode = binRequiredCode(err);
    if (binCode === null) return false;
    setPending({ binCode, mark });
    return true;
  }

  return {
    confirmedBin,
    /** Адрес, который ждёт отложенная отметка; `null` — не ждёт. */
    binRequest: pending ? pending.binCode : null,
    lotMismatch,
    execute,
    executePick: (itemId: string, qty: number) => executePick(itemId, qty, confirmedBin),
    deferIfBinRequired,
    /** Новый скан товара или ручная отметка — старая отложенная отметка не нужна. */
    clearPending: () => setPending(null),
    /** Забрать «партия другая» для подтверждения / отмены. */
    takeLotMismatch: () => {
      const current = lotMismatch;
      setLotMismatch(null);
      return current;
    },
  };
}
